import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ticketId", "threadId"];
import { registerMutation, useMutation } from 'data-fetching-client';
import { useCallback, useEffect, useState } from 'react';
import { generatePath, useHistory } from 'quartz-routing/react-router';
import { convertToHtml, convertToPlaintext } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { GET_TICKET_THREAD, GET_TICKET_THREAD_KEY } from './useTicketThread';
import { GET_THREAD_ATTACHMENTS_QUERY } from './useThreadAttachments';
import { useTicketThread } from './useTicketThread';
import { MESSAGE_CHAR_LIMIT, UnpublishableReasonEnum } from '../constants';
import { FileUploadStatus } from './types';
import { ROUTES } from '../constants/routes';
import { publishMessageRpc, publishMessageToNewThreadRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/MessagePublishingRpc';
import { chirpClient } from '../utils/chirpClient';
import { getPortalAccountId, getPortalInstanceId } from 'portals-platform-lib';
const toCommonMessage = msg => Object.assign({}, msg, {
  '@type': 'COMMON_MESSAGE'
});
const publishMessage = _ref => {
  let {
      ticketId,
      threadId
    } = _ref,
    payload = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (threadId != null) {
    return chirpClient.call(publishMessageRpc, {
      request: {
        accountId: getPortalAccountId(),
        portalInstanceId: getPortalInstanceId(),
        params: Object.assign({
          ticketId,
          threadId
        }, payload)
      }
    });
  }
  return chirpClient.call(publishMessageToNewThreadRpc, {
    request: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      params: Object.assign({
        ticketId
      }, payload)
    }
  });
};
const PUBLISH_MESSAGE_MUTATION_FIELD_NAME = 'publishMessage';
const PUBLISH_MESSAGE_MUTATION = registerMutation({
  fieldName: PUBLISH_MESSAGE_MUTATION_FIELD_NAME,
  fetcher: publishMessage,
  args: ['ticketId', 'threadId', 'message', 'richMessage', 'fileIds', 'inReplyToId']
});
export function usePublishMessage({
  ticketId,
  threadId,
  richEditorContent,
  uploads,
  clearEditor
}) {
  const history = useHistory();
  const {
    lastMessageId: inReplyToId
  } = useTicketThread(ticketId, threadId);
  const [mutate, {
    loading,
    error
  }] = useMutation(PUBLISH_MESSAGE_MUTATION, {
    update: (cache, {
      data
    }) => {
      if (!threadId) {
        return;
      }
      if (!(data !== null && data !== void 0 && data[PUBLISH_MESSAGE_MUTATION_FIELD_NAME])) {
        return;
      }
      const result = data[PUBLISH_MESSAGE_MUTATION_FIELD_NAME];
      const {
        message,
        responder
      } = result;
      const uploadedFiles = uploads.filter(upload => upload.status === FileUploadStatus.UPLOADED);
      cache.updateQuery({
        query: GET_TICKET_THREAD,
        variables: {
          ticketId,
          threadId: threadId
        }
      }, existingData => {
        const cachedThread = existingData === null || existingData === void 0 ? void 0 : existingData[GET_TICKET_THREAD_KEY];
        if (cachedThread) {
          const updatedAttachments = Object.assign({}, cachedThread.attachments);
          if (uploadedFiles.length > 0) {
            uploadedFiles.forEach(file => {
              if (!cachedThread.attachments[file.id]) {
                updatedAttachments[file.id] = {
                  attachmentTimestamp: Date.now(),
                  fileMetadata: {
                    expiresAt: file.expiresAt,
                    url: file.downloadUrl,
                    name: file.fileName,
                    extension: file.fileExtension,
                    type: file.fileType,
                    size: file.fileSize,
                    height: file.imageHeight,
                    width: file.imageWidth
                  }
                };
              }
            });
          }
          const updatedResponders = Object.assign({}, cachedThread.responders);
          if (responder && message.sender) {
            updatedResponders[JSON.stringify(message.sender)] = responder;
          }
          const updatedThread = Object.assign({}, cachedThread, {
            responders: updatedResponders,
            attachments: updatedAttachments,
            results: [toCommonMessage(message), ...(cachedThread.results || [])]
          });
          return {
            [GET_TICKET_THREAD_KEY]: updatedThread
          };
        }
        return existingData;
      });
      cache.updateQuery({
        query: GET_THREAD_ATTACHMENTS_QUERY,
        variables: {
          params: {
            ticketId,
            threadId: threadId
          }
        }
      }, existingData => {
        const cachedAttachments = existingData === null || existingData === void 0 ? void 0 : existingData.result;
        if (!cachedAttachments || uploadedFiles.length === 0) {
          return existingData;
        }
        const existingAttachments = cachedAttachments.attachments;
        const latestAttachmentTimestamp = existingAttachments.reduce((latestTimestamp, attachment) => Math.max(latestTimestamp, attachment.attachmentTimestamp), 0);
        if (message.timestamp <= latestAttachmentTimestamp) {
          return existingData;
        }
        const newAttachments = uploadedFiles.map(file => ({
          attachmentTimestamp: message.timestamp,
          fileMetadata: {
            expiresAt: file.expiresAt,
            url: file.downloadUrl,
            name: file.fileName,
            extension: file.fileExtension,
            type: file.fileType,
            size: file.fileSize,
            height: file.imageHeight,
            width: file.imageWidth
          }
        }));
        return {
          result: Object.assign({}, cachedAttachments, {
            attachments: [...newAttachments, ...existingAttachments]
          })
        };
      });
    },
    onCompleted: () => {
      clearEditor();
      if (!threadId) {
        const params = new URLSearchParams(history.location.search);
        params.set('creatingThread', '1');
        history.replace({
          pathname: generatePath(ROUTES.ticket, {
            ticketId
          }),
          search: `?${params.toString()}`
        });
      }
    }
  });
  const [isPublishable, setIsPublishable] = useState(false);
  const [unpublishableReason, setUnpublishableReason] = useState(UnpublishableReasonEnum.NO_CONTENT);
  const handlePublishMessage = useCallback(() => {
    if (!isPublishable || !richEditorContent) {
      return;
    }
    const htmlContent = convertToHtml(richEditorContent);
    const plaintextContent = convertToPlaintext(richEditorContent);
    const fileIds = uploads.filter(upload => upload.status === FileUploadStatus.UPLOADED).map(upload => upload.id);
    const payload = Object.assign({
      ticketId,
      threadId,
      inReplyToId,
      fileIds,
      message: plaintextContent
    }, htmlContent && {
      richMessage: htmlContent
    });
    mutate({
      variables: payload
    }).catch(() => {});
  }, [inReplyToId, ticketId, threadId, isPublishable, mutate, richEditorContent, uploads]);
  useEffect(() => {
    const plainTextContent = convertToPlaintext(richEditorContent);
    const htmlContent = convertToHtml(richEditorContent);
    const hasMessage = plainTextContent.trim() !== '';
    const hasUploadedFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADED);
    const hasUploadingFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADING || upload.status === FileUploadStatus.PENDING);
    const hasFailedUploads = uploads.some(upload => upload.status === FileUploadStatus.ERROR);
    const validMessageLength = htmlContent.length <= MESSAGE_CHAR_LIMIT;
    let unpublishable = null;
    if (!(hasMessage || hasUploadedFiles)) {
      unpublishable = UnpublishableReasonEnum.NO_CONTENT;
    } else if (hasFailedUploads) {
      unpublishable = UnpublishableReasonEnum.HAS_FAILED_UPLOADS;
    } else if (hasUploadingFiles) {
      unpublishable = UnpublishableReasonEnum.UPLOADING;
    } else if (!validMessageLength) {
      unpublishable = UnpublishableReasonEnum.EXCEEDS_CHAR_LIMIT;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUnpublishableReason(unpublishable);
    setIsPublishable(unpublishable === null);
  }, [richEditorContent, uploads]);
  return {
    publishMessage: handlePublishMessage,
    publishable: {
      isPublishable,
      unpublishableReason
    },
    isPublishingMessage: loading,
    error
  };
}